package diana.soleil.design.iraniancanadienbazzar.core.data.firestore

import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.AdminRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CampaignChannel
import diana.soleil.design.iraniancanadienbazzar.core.data.CampaignSendResult
import diana.soleil.design.iraniancanadienbazzar.core.data.OwnerRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AuthService
import diana.soleil.design.iraniancanadienbazzar.core.firebase.FirebaseServices
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessClaim
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingChangeRequest
import diana.soleil.design.iraniancanadienbazzar.core.model.PendingContentItem
import diana.soleil.design.iraniancanadienbazzar.core.model.PendingContentKind
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingRequest
import diana.soleil.design.iraniancanadienbazzar.core.model.ModerationAction
import diana.soleil.design.iraniancanadienbazzar.core.model.Report
import diana.soleil.design.iraniancanadienbazzar.core.model.DailyPlatformCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.UserAccount
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.FieldValue
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map

/**
 * Admin + moderation: the reports queue, approvals console, owner tools.
 *
 * Split out of the former 2,753-line FirestoreRepositories.kt (2026-08-02) — same code,
 * same behaviour, one file per domain. Shared collection names and helpers live in
 * FirestoreCommon.kt.
 */

/** Live [ReportRepository]: create-only writes; the admin queue consumes them. */
class FirestoreReportRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : ReportRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    override suspend fun fileReport(targetType: String, targetId: String, reason: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        if (reason.isBlank()) return DataResult.Failure(AppError.Unknown())
        return try {
            firestore.collection(REPORTS).add(
                ReportCreateDoc(
                    targetType = targetType,
                    targetId = targetId,
                    reporterUid = user.uid,
                    reason = reason.trim(),
                    status = "open",
                    createdAt = Timestamp.ServerTimestamp,
                ),
            )
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }
}

/**
 * Live [OwnerRepository] over Cloud Firestore (GitLive). The owned-listings query filters on
 * `ownerUid == uid` (owner read path in rules covers every status).
 */
class FirestoreOwnerRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : OwnerRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeOwnedBusinesses(): Flow<DataResult<List<Business>>> =
        auth.authState.flatMapLatest { user ->
            if (user == null || user.isAnonymous) {
                kotlinx.coroutines.flow.flowOf(DataResult.Failure(AppError.Unauthorized()))
            } else {
                firestore.collection(BUSINESSES).where { "ownerUid" equalTo user.uid }.snapshots
                    .map { snap ->
                        val businesses = snap.documents.mapNotNull { doc ->
                            runCatching { doc.decode<BusinessDoc>().toModel(doc.id) }.getOrNull()
                        }
                        DataResult.Success(businesses) as DataResult<List<Business>>
                    }
                    .catch { emit(DataResult.Failure(it.toAppError())) }
            }
        }

    override suspend fun replyToReview(reviewId: String, replyText: String): DataResult<Unit> = try {
        // changedOnly(['ownerReply']) in rules — merge-set touches exactly that field.
        firestore.collection(REVIEWS).document(reviewId)
            .set(ReviewOwnerReplyDoc(ownerReply = replyText.trim().ifEmpty { null }), merge = true)
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }
}

/**
 * Live [AdminRepository] over Cloud Firestore (GitLive). Every operation rides an admin-only rules
 * path (`users/{uid}.role == 'admin'`), so a non-admin caller gets a permission failure from the
 * server regardless of what the UI shows.
 */
class FirestoreAdminRepository(
    private val firebase: FirebaseServices,
    private val auth: AuthService,
) : AdminRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    override val currentUid: String? get() = auth.currentUser?.uid

    override fun observePendingBusinesses(): Flow<DataResult<List<Business>>> =
        firestore.collection(BUSINESSES).where { "status" equalTo STATUS_PENDING }.snapshots
            .map { snap ->
                val businesses = snap.documents.mapNotNull { doc ->
                    runCatching { doc.decode<BusinessDoc>().toModel(doc.id) }.getOrNull()
                }
                DataResult.Success(businesses) as DataResult<List<Business>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override fun searchBusinessesByName(prefix: String, limit: Int): Flow<DataResult<List<Business>>> {
        val q = prefix.trim()
        if (q.isEmpty()) return flowOf(DataResult.Success(emptyList()))
        // Firestore has no case-insensitive prefix match; names are stored as written, so probe the
        // spellings people type: as typed, Title-case first letter, all lower, all upper.
        val variants = listOf(q, q.replaceFirstChar { it.uppercase() }, q.lowercase(), q.uppercase()).distinct()
        val streams = variants.map { v ->
            firestore.collection(BUSINESSES)
                .orderBy("name", Direction.ASCENDING)
                .startAt(v)
                .endAt(v + PREFIX_END)
                .limit(limit)
                .snapshots
                .map { snap ->
                    snap.documents.mapNotNull { doc -> runCatching { doc.decode<BusinessDoc>().toModel(doc.id) }.getOrNull() }
                }
        }
        return combine(streams) { lists ->
            DataResult.Success(lists.flatMap { it }.distinctBy { it.id }.sortedBy { it.name.lowercase() }.take(limit)) as DataResult<List<Business>>
        }.catch { emit(DataResult.Failure(it.toAppError())) }
    }

    override fun observeAllBusinesses(): Flow<DataResult<List<Business>>> =
        // Recent window (newest first) so the admin browser isn't loading the entire directory; the
        // console searches client-side over this. orderBy(createdAt) is single-field auto-indexed.
        firestore.collection(BUSINESSES)
            .orderBy("createdAt", Direction.DESCENDING).limit(200).snapshots
            .map { snap ->
                val businesses = snap.documents.mapNotNull { doc ->
                    runCatching { doc.decode<BusinessDoc>().toModel(doc.id) }.getOrNull()
                }
                DataResult.Success(businesses) as DataResult<List<Business>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override fun observeEditedBusinesses(): Flow<DataResult<List<Business>>> =
        // Single-field equality → auto-indexed; the Function only ever sets it on approved docs.
        firestore.collection(BUSINESSES).where { "editedSinceApproval" equalTo true }.snapshots
            .map { snap ->
                val businesses = snap.documents.mapNotNull { doc ->
                    runCatching { doc.decode<BusinessDoc>().toModel(doc.id) }.getOrNull()
                }
                DataResult.Success(businesses) as DataResult<List<Business>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override suspend fun setBusinessStatus(businessId: String, status: BusinessStatus, reason: String?): DataResult<Unit> = try {
        val wire = when (status) {
            BusinessStatus.Approved -> "approved"
            BusinessStatus.Rejected -> "rejected"
            BusinessStatus.Suspended -> "suspended"
            BusinessStatus.Pending -> "pending"
        }
        val trimmedReason = reason?.trim()?.take(REJECTION_REASON_MAX)?.takeIf { it.isNotBlank() }
        // EVERY admin status decision clears the BIZ-5 edited-since-approval flag — approve IS the
        // re-review, and reject/suspend must not leave the doc stranded in the edited queue forever
        // (the queue filters solely on the flag; QA round-7 HIGH finding).
        if (status == BusinessStatus.Rejected && trimmedReason != null) {
            // Reason stored on the doc so the outcome Function folds it into the notification.
            firestore.collection(BUSINESSES).document(businessId)
                .update("status" to wire, "rejectionReason" to trimmedReason, "editedSinceApproval" to false)
        } else {
            firestore.collection(BUSINESSES).document(businessId)
                .update("status" to wire, "editedSinceApproval" to false)
        }
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override fun observeOpenReports(): Flow<DataResult<List<Report>>> =
        firestore.collection(REPORTS).where { "status" equalTo REPORT_OPEN }.snapshots
            .map { snap ->
                val reports = snap.documents.mapNotNull { doc ->
                    runCatching { doc.decode<ReportDoc>().toModel(doc.id) }.getOrNull()
                }
                DataResult.Success(reports) as DataResult<List<Report>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override suspend fun resolveReport(reportId: String): DataResult<Unit> = try {
        firestore.collection(REPORTS).document(reportId).update("status" to "resolved")
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override fun observeListingRequests(): Flow<DataResult<List<ListingRequest>>> =
        firestore.collection(LISTING_REQUESTS).where { "status" equalTo REPORT_OPEN }.snapshots
            .map { snap ->
                val requests = snap.documents.mapNotNull { doc ->
                    runCatching { doc.decode<ListingRequestDoc>().toModel(doc.id) }.getOrNull()
                }.sortedByDescending { it.createdAtMillis ?: 0L }
                DataResult.Success(requests) as DataResult<List<ListingRequest>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override suspend fun resolveListingRequest(requestId: String): DataResult<Unit> = try {
        firestore.collection(LISTING_REQUESTS).document(requestId).update("status" to "resolved")
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override fun observePendingEvents(): Flow<DataResult<List<Event>>> =
        firestore.collection(EVENTS).where { "status" equalTo "pending" }.snapshots
            .map { snap ->
                val events = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<EventDoc>().toModel(doc.id) }.getOrNull() }
                    .sortedBy { it.startsAtMillis }
                DataResult.Success(events) as DataResult<List<Event>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override suspend fun setEventStatus(eventId: String, approved: Boolean, reason: String?): DataResult<Unit> = try {
        val trimmedReason = reason?.trim()?.take(REJECTION_REASON_MAX)?.takeIf { it.isNotBlank() }
        if (!approved && trimmedReason != null) {
            firestore.collection(EVENTS).document(eventId)
                .update("status" to "rejected", "rejectionReason" to trimmedReason)
        } else {
            firestore.collection(EVENTS).document(eventId)
                .update("status" to if (approved) "approved" else "rejected")
        }
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override fun observeClaims(): Flow<DataResult<List<BusinessClaim>>> =
        firestore.collection(CLAIMS).where { "status" equalTo "pending" }.snapshots
            .map { snap ->
                // Single-field equality query → auto-indexed; newest-first ordering is done client-side
                // so a just-created doc with a still-resolving server timestamp isn't dropped.
                val claims = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<ClaimDoc>().toModel(doc.id) }.getOrNull() }
                    .sortedByDescending { it.createdAtMillis ?: Long.MAX_VALUE }
                DataResult.Success(claims) as DataResult<List<BusinessClaim>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override suspend fun approveClaim(claim: BusinessClaim): DataResult<Unit> = try {
        // One atomic batch: sequential writes could transfer ownership and then fail to close the
        // claim — a second admin tap (or a rival pending claim) would re-run against a stale queue.
        val batch = firestore.batch()
        batch.update(
            firestore.collection(BUSINESSES).document(claim.businessId),
            "ownerUid" to claim.claimantUid,
            "claimStatus" to "claimed",
            "claimRequestedBy" to null,
        )
        batch.update(
            firestore.collection(CLAIMS).document(claim.id),
            "status" to "approved",
            "reviewedBy" to currentUid,
            "reviewedAt" to FieldValue.serverTimestamp,
        )
        batch.commit()
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun rejectClaim(claim: BusinessClaim): DataResult<Unit> = try {
        firestore.collection(CLAIMS).document(claim.id).update(
            "status" to "rejected",
            "reviewedBy" to currentUid,
            "reviewedAt" to FieldValue.serverTimestamp,
        )
        // Return the listing to unclaimed so it stays claimable by someone else.
        runCatching {
            firestore.collection(BUSINESSES).document(claim.businessId).update(
                "claimStatus" to "unclaimed",
                "claimRequestedBy" to null,
            )
        }
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override fun observePendingListingChanges(): Flow<DataResult<List<ListingChangeRequest>>> =
        firestore.collection(LISTING_CHANGE_REQUESTS).where { "status" equalTo STATUS_PENDING }.snapshots
            .map { snap ->
                val requests = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<ListingChangeRequestDoc>().toModel(doc.id) }.getOrNull() }
                    .sortedBy { it.createdAtMillis ?: Long.MAX_VALUE }
                DataResult.Success(requests) as DataResult<List<ListingChangeRequest>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override suspend fun approveListingChange(request: ListingChangeRequest): DataResult<Unit> = try {
        val updates = buildList<Pair<String, Any?>> {
            request.proposedName?.let { name ->
                add("name" to name)
                // Old per-language spellings would keep showing the OLD name in FR/FA.
                add("nameLocalized" to mapOf("en" to name))
            }
            request.proposedCategoryId?.let { newCategory ->
                add("categoryId" to newCategory)
                // Drop answers the new category doesn't ask about (the batch can't read the doc, so remove
                // every key the new category disallows — arrayRemove ignores keys that aren't there).
                val registry = diana.soleil.design.iraniancanadienbazzar.core.model.BusinessAttributeRegistry
                val allowed = registry.forCategory(newCategory).map { it.key }.toSet()
                val stale = (registry.allKeys - allowed).toTypedArray()
                if (stale.isNotEmpty()) add("attributes" to FieldValue.arrayRemove(*stale))
            }
            request.proposedAddress?.let { address ->
                add("address.line" to address.line)
                add("address.city" to address.city)
                add("address.province" to address.province)
                add("address.postalCode" to address.postalCode)
                add("address.lat" to null)
                add("address.lng" to null)
                diana.soleil.design.iraniancanadienbazzar.core.model.Metros.forCity(address.city)?.let { add("metroId" to it.id) }
            }
        }
        val batch = firestore.batch()
        if (updates.isNotEmpty()) {
            batch.update(firestore.collection(BUSINESSES).document(request.businessId), *updates.toTypedArray())
        }
        batch.update(
            firestore.collection(LISTING_CHANGE_REQUESTS).document(request.id),
            "status" to "approved",
            "reviewedBy" to currentUid,
            "reviewedAt" to FieldValue.serverTimestamp,
        )
        batch.commit()
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun rejectListingChange(request: ListingChangeRequest, reason: String?): DataResult<Unit> = try {
        firestore.collection(LISTING_CHANGE_REQUESTS).document(request.id).update(
            "status" to "rejected",
            "rejectionReason" to reason?.trim()?.take(REJECTION_REASON_MAX)?.takeIf { it.isNotBlank() },
            "reviewedBy" to currentUid,
            "reviewedAt" to FieldValue.serverTimestamp,
        )
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override fun observePendingContent(): Flow<DataResult<List<PendingContentItem>>> {
        fun hidden(kind: PendingContentKind, build: (dev.gitlive.firebase.firestore.DocumentSnapshot) -> PendingContentItem?) =
            firestore.collection(kind.collection).where { "visible" equalTo false }.snapshots
                .map { snap -> snap.documents.mapNotNull { doc -> runCatching { build(doc) }.getOrNull() } }
        val news = hidden(PendingContentKind.News) { doc ->
            val d = doc.decode<NewsDoc>()
            PendingContentItem(
                PendingContentKind.News, doc.id, title = d.title.toModel(),
                text = d.summary.toModel(), imageUrl = d.imageUrl, linkUrl = d.linkUrl,
            )
        }
        val jokes = hidden(PendingContentKind.Joke) { doc ->
            PendingContentItem(PendingContentKind.Joke, doc.id, text = doc.decode<JokeDoc>().text.toModel())
        }
        val recipes = hidden(PendingContentKind.Recipe) { doc ->
            val d = doc.decode<RecipeDoc>()
            PendingContentItem(
                PendingContentKind.Recipe, doc.id, title = d.title.toModel(),
                text = d.summary.toModel(), imageUrl = d.imageUrl,
            )
        }
        val facts = hidden(PendingContentKind.Fact) { doc ->
            PendingContentItem(PendingContentKind.Fact, doc.id, text = doc.decode<FactDoc>().text.toModel())
        }
        return combine(news, jokes, recipes, facts) { n, j, r, f ->
            DataResult.Success(n + j + r + f) as DataResult<List<PendingContentItem>>
        }.catch { emit(DataResult.Failure(it.toAppError())) }
    }

    override suspend fun publishContent(item: PendingContentItem): DataResult<Unit> = try {
        val ref = firestore.collection(item.kind.collection).document(item.id)
        if (item.kind == PendingContentKind.News) {
            ref.update("visible" to true)
        } else {
            // The gems readers query visible == true AND status == visible.
            ref.update("visible" to true, "status" to "visible")
        }
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun discardContent(item: PendingContentItem): DataResult<Unit> = try {
        firestore.collection(item.kind.collection).document(item.id).delete()
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun hideReview(reviewId: String): DataResult<Unit> = try {
        firestore.collection(REVIEWS).document(reviewId).update("status" to "removed")
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun removePost(postId: String): DataResult<Unit> = try {
        firestore.collection(POSTS).document(postId).delete()
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun removeMarketplaceItem(itemId: String): DataResult<Unit> = try {
        firestore.collection(MARKETPLACE).document(itemId).update("status" to "removed")
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun restoreReview(reviewId: String): DataResult<Unit> = try {
        firestore.collection(REVIEWS).document(reviewId).update("status" to REVIEW_VISIBLE)
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun restoreMarketplaceItem(itemId: String): DataResult<Unit> = try {
        firestore.collection(MARKETPLACE).document(itemId).update("status" to "active")
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun logModerationAction(
        action: String,
        targetType: String,
        targetId: String,
        reason: String?,
    ): DataResult<Unit> = try {
        val actor = currentUid
        if (actor == null) {
            DataResult.Failure(AppError.Unauthorized())
        } else {
            firestore.collection(MODERATION_ACTIONS).add(
                ModerationActionCreateDoc(
                    actorUid = actor,
                    action = action,
                    targetType = targetType,
                    targetId = targetId,
                    reason = reason?.takeIf { it.isNotBlank() },
                    createdAt = Timestamp.ServerTimestamp,
                ),
            )
            DataResult.Success(Unit)
        }
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override fun observeModerationLog(): Flow<DataResult<List<ModerationAction>>> =
        firestore.collection(MODERATION_ACTIONS)
            .orderBy("createdAt", Direction.DESCENDING).limit(200).snapshots
            .map { snap ->
                val actions = snap.documents.mapNotNull { doc ->
                    runCatching { doc.decode<ModerationActionDoc>().toModel(doc.id) }.getOrNull()
                }
                DataResult.Success(actions) as DataResult<List<ModerationAction>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override fun observeMyRole(): Flow<String?> {
        val me = currentUid ?: return flowOf(null)
        return firestore.collection(USERS).document(me).snapshots
            .map { snap -> if (snap.exists) snap.decode<UserRoleDoc>().role else null }
            .catch { emit(null) }
    }

    override fun observeUsers(): Flow<DataResult<List<UserAccount>>> =
        firestore.collection(USERS).limit(500).snapshots
            .map { snap ->
                val users = snap.documents.mapNotNull { doc ->
                    runCatching { doc.decode<UserAccountDoc>().toModel(doc.id) }.getOrNull()
                }.sortedWith(
                    compareByDescending<UserAccount> { it.isAdmin }
                        .thenBy { it.displayName ?: it.uid },
                )
                DataResult.Success(users) as DataResult<List<UserAccount>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override suspend fun setUserRole(uid: String, role: String): DataResult<Unit> = try {
        firestore.collection(USERS).document(uid).update("role" to role)
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override fun observeGrowthDaily(): Flow<DataResult<List<DailyPlatformCounters>>> =
        // The collection grows one doc per day forever — cap at the trailing ~6 months (newest
        // first server-side, re-sorted ascending for the chart) instead of streaming all history.
        firestore.collection("platform_metrics")
            .orderBy("day", Direction.DESCENDING)
            .limit(180)
            .snapshots
            .map { snap ->
                val days = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<PlatformDailyDoc>().toModel() }.getOrNull() }
                    .filter { it.day.isNotBlank() }
                    .sortedBy { it.day }
                DataResult.Success(days) as DataResult<List<DailyPlatformCounters>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override suspend fun sendCampaign(
        campaignId: String,
        metroId: String?,
        title: LocalizedText,
        body: LocalizedText,
        link: String,
        channel: CampaignChannel,
    ): DataResult<CampaignSendResult> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            // Admin-gated server-side (the callable re-checks users/{uid}.role == 'admin').
            val result = firebase.functions.httpsCallable("sendCampaign").invoke(
                mapOf(
                    "campaignId" to campaignId,
                    "metroId" to metroId,
                    "title" to mapOf("en" to title.en, "fr" to title.fr, "fa" to title.fa),
                    "body" to mapOf("en" to body.en, "fr" to body.fr, "fa" to body.fa),
                    "link" to link,
                    "channel" to channel.wire,
                ),
            )
            // The invoke succeeded → the campaign ran; count/capped are best-effort display data.
            val payload = runCatching { result.data<Map<String, Any?>>() }.getOrNull().orEmpty()
            DataResult.Success(
                CampaignSendResult(
                    sent = payload["sent"]?.toString()?.toIntOrNull() ?: 0,
                    capped = payload["capped"]?.toString() == "true",
                ),
            )
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }
}

/** High code point that sorts after every real character — closes a Firestore prefix range. */
private const val PREFIX_END = "\uf8ff"
